import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Avatar,
  LinearProgress,
  Alert,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material'
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  Group as GroupIcon,
  Email as EmailIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  BusinessCenter as BusinessCenterIcon,
} from '@mui/icons-material'
import { colors } from '../theme'
import { clientsApi, Client } from '../services/api'
import { useAtom } from 'jotai'
import { userAtom } from '../state/auth'
import { workflowStageDefinitionsAtom, isJobSearchEligible } from '../state/clients'
import AssignTeamDialog from '../components/clients/AssignTeamDialog'
import AddClientDialog, { NewClientData } from '../components/clients/AddClientDialog'
import LoadingSpinner from '../components/LoadingSpinner'
import PageHelpButton from '../components/PageHelpButton'
import { useNotification } from '../hooks/useNotification'
import { isAdminOrManager } from '../utils/roles'

// Fallback labels - dynamic labels from workflowStageDefinitionsAtom are preferred
const fallbackStageLabels: Record<string, string> = {
  onboarding: 'Onboarding',
  pre_north_star: 'Initial Consultation',
  north_star: 'North Star',
  strategy_session: 'Strategy Session',
  intake: 'Intake',
  dream_job: 'Dream Job',
  clifton_strengths: 'Clifton Strengths',
  resume_creation: 'Resume Writing',
  resume_writing: 'Resume Writing',
  deliverables: 'Core Deliverables',
  linkedin_optimization: 'LinkedIn Prep',
  linkedin_prep: 'LinkedIn Prep',
  linkedin_workshop: 'LinkedIn Workshop',
  job_search: 'Go-To-Market',
  job_hunt: 'Job Hunt',
  market_ready: 'Market Ready',
  interview_prep: 'Interview Prep',
  offer_negotiation: 'Offer Negotiation',
  placement: 'Placement',
  complete: 'Complete',
  completed: 'Complete',
}

const stageColors: Record<string, string> = {
  onboarding: '#9e9e9e',
  north_star: '#6366f1',
  strategy_session: '#8b5cf6',
  intake: '#f59e0b',
  dream_job: '#a855f7',
  clifton_strengths: '#ec4899',
  resume_creation: colors.primary,
  resume_writing: colors.primary,
  deliverables: colors.primary,
  linkedin_optimization: colors.primaryLight,
  linkedin_prep: colors.primaryLight,
  linkedin_workshop: colors.primaryLight,
  job_search: colors.secondary,
  job_hunt: colors.secondary,
  market_ready: '#0ea5e9',
  interview_prep: '#06b6d4',
  offer_negotiation: '#f97316',
  placement: '#84cc16',
  complete: '#10b981',
  completed: '#10b981',
}

export default function Clients() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [user] = useAtom(userAtom)
  const [workflowStages] = useAtom(workflowStageDefinitionsAtom)

  // Build stage labels dynamically from DB definitions, with hardcoded fallback
  const stageLabels = useMemo(() => {
    if (workflowStages.length > 0) {
      const dynamic = Object.fromEntries(workflowStages.filter((s) => s.isActive).map((s) => [s.stageId, s.label]))
      return { ...fallbackStageLabels, ...dynamic }
    }
    return fallbackStageLabels
  }, [workflowStages])
  const [searchQuery, setSearchQuery] = useState('')
  const [clients, setClients] = useState<Client[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Check if filtering by needsTeamAssignment
  const filterNeedsAssignment = searchParams.get('filter') === 'needsTeamAssignment'

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [selectedClient, setSelectedClient] = useState<Client | null>(null)

  // Assign team dialog state
  const [assignTeamDialogOpen, setAssignTeamDialogOpen] = useState(false)

  // Add client dialog state
  const [addClientDialogOpen, setAddClientDialogOpen] = useState(false)

  // Delete client state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null)
  const [deleting, setDeleting] = useState(false)

  const { showSuccess, showError } = useNotification()
  const isManager = isAdminOrManager(user)

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, client: Client) => {
    event.stopPropagation()
    setMenuAnchor(event.currentTarget)
    setSelectedClient(client)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
  }

  const handleDeleteClick = () => {
    setClientToDelete(selectedClient)
    setDeleteDialogOpen(true)
    handleMenuClose()
  }

  const handleDeleteConfirm = async () => {
    if (!clientToDelete) return

    try {
      setDeleting(true)
      await clientsApi.delete(clientToDelete.id)
      setClients((prev) => prev.filter((c) => c.id !== clientToDelete.id))
      showSuccess(`${clientToDelete.name} has been deleted`)
      setDeleteDialogOpen(false)
      setClientToDelete(null)
    } catch (err) {
      console.error('Error deleting client:', err)
      showError(err instanceof Error ? err.message : 'Failed to delete client')
    } finally {
      setDeleting(false)
    }
  }

  const handleAssignTeamClick = () => {
    setAssignTeamDialogOpen(true)
    handleMenuClose()
  }

  const handleTeamAssigned = (updatedClient: Client) => {
    setClients((prev) =>
      prev.map((c) => (c.id === updatedClient.id ? updatedClient : c))
    )
  }

  const handleAddClient = async (clientData: NewClientData) => {
    try {
      const newClient = await clientsApi.create(clientData)
      setClients((prev) => {
        if (prev.some((c) => c.id === newClient.id)) return prev
        return [newClient, ...prev]
      })
      setAddClientDialogOpen(false)
    } catch (err) {
      console.error('Error adding client:', err)
      throw err
    }
  }

  const loadClients = async () => {
    try {
      setLoading(true)
      setError(null)
      const clientList = filterNeedsAssignment
        ? await clientsApi.getNeedingTeamAssignment()
        : await clientsApi.list({ all: true })
      setClients(clientList ?? [])
    } catch (err) {
      console.error('Error loading clients:', err)
      setError(err instanceof Error ? err.message : 'Failed to load clients')
    } finally {
      setLoading(false)
    }
  }

  const clearFilter = () => {
    setSearchParams({})
  }

  useEffect(() => {
    loadClients()
  }, [filterNeedsAssignment])

  const filteredClients = clients.filter(
    (client) =>
      client.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // The stage list Stage Gates configures, derived exactly as the pipeline
  // board derives it, so the bar and the board cannot disagree. The list this
  // replaced named a stage vocabulary the app stopped using: of its eleven ids
  // only 'onboarding' still existed, and that one sat at index 0, so every
  // client on this page showed 0%.
  //
  // The filter is belt-and-braces, not the thing that makes this right:
  // list-workflow-stages reads the isActive-sortOrder GSI, so the atom already
  // holds only active stages, in order, and isActive arrives as the STRING
  // 'true' — which is why a truthiness test on it cannot be relied on alone.
  const orderedStageIds = useMemo(
    () =>
      workflowStages
        .filter((stage) => stage.isActive)
        .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder))
        .map((stage) => stage.stageId),
    [workflowStages]
  )

  // null rather than 0 when the stage has no place on the line — a client in a
  // deactivated stage, or stages not loaded yet. An empty bar would claim no
  // progress had been made, which is a different statement from not knowing.
  const calculateProgress = (client: Client): number | null => {
    if (orderedStageIds.length < 2) return null
    const currentIndex = orderedStageIds.indexOf(client.workflowStage || '')
    if (currentIndex === -1) return null
    return Math.round((currentIndex / (orderedStageIds.length - 1)) * 100)
  }

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, sm: 3 }, flexWrap: 'wrap', gap: { xs: 1, sm: 1 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>Clients</Typography>
          <PageHelpButton articleId="page-clients-list" />
          {filterNeedsAssignment && (
            <Chip
              label="Needs Team Assignment"
              color="warning"
              onDelete={clearFilter}
              icon={<GroupIcon />}
              size={isMobile ? 'small' : 'medium'}
              sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon sx={{ display: { xs: 'none', sm: 'inline-block' } }} />}
            onClick={loadClients}
            disabled={loading}
            size={isMobile ? 'small' : 'medium'}
            sx={{ minWidth: { xs: 'auto', sm: 'auto' } }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Refresh</Box>
            <RefreshIcon sx={{ display: { xs: 'inline-block', sm: 'none' } }} />
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ display: { xs: 'none', sm: 'inline-block' } }} />}
            onClick={() => setAddClientDialogOpen(true)}
            size={isMobile ? 'small' : 'medium'}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Add Client</Box>
            <AddIcon sx={{ display: { xs: 'inline-block', sm: 'none' } }} />
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            <TextField
              placeholder="Search clients..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: '100%', sm: 300 } }}
            />
          </Box>

          {filteredClients.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                {clients.length === 0 ? 'No clients yet' : 'No clients match your search'}
              </Typography>
            </Box>
          ) : isMobile ? (
            // Mobile Card Layout
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {filteredClients.map((client) => {
                const progress = client.progress ?? calculateProgress(client)
                const stage = client.workflowStage || 'onboarding'
                return (
                  <Card
                    key={client.id}
                    variant="outlined"
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'grey.50' },
                    }}
                    onClick={() => navigate(`/clients/${client.id}`)}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                        <Avatar sx={{ bgcolor: colors.primary, width: 44, height: 44, fontSize: '1rem' }}>
                          {getInitials(client.name || 'C')}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
                                {client.name}
                              </Typography>
                              {client.email && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                  <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                    {client.email}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, client)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          <Chip
                            label={stageLabels[stage] || stage}
                            size="small"
                            sx={{
                              backgroundColor: stageColors[stage] || '#9e9e9e',
                              color: 'white',
                              height: 20,
                              fontSize: '0.7rem',
                              mb: 1,
                            }}
                          />
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip
                            label={`Resume: ${(client.resumeStatus || 'not_started').replace(/_/g, ' ')}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 24 }}
                          />
                          <Chip
                            label={`LinkedIn: ${(client.linkedinStatus || 'not_started').replace(/_/g, ' ')}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 24 }}
                          />
                        </Box>

                        <Box>
                          {progress === null ? (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              Stage is not on the pipeline
                            </Typography>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{
                                  flexGrow: 1,
                                  height: 6,
                                  borderRadius: 3,
                                  backgroundColor: colors.gray200,
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: colors.primary,
                                  },
                                }}
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', minWidth: 35 }}>
                                {progress}%
                              </Typography>
                            </Box>
                          )}
                          {client.assignedTo && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              Assigned to: {client.assignedTo}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                )
              })}
            </Box>
          ) : (
            // Desktop Table Layout
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Client</TableCell>
                    <TableCell>Workflow Stage</TableCell>
                    <TableCell>Resume</TableCell>
                    <TableCell>LinkedIn</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Assigned To</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredClients.map((client) => {
                    const progress = client.progress ?? calculateProgress(client)
                    const stage = client.workflowStage || 'onboarding'
                    return (
                      <TableRow key={client.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/clients/${client.id}`)}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: colors.primary, color: 'white' }}>
                              {client.name?.[0] || '?'}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2">{client.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {client.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={stageLabels[stage] || stage}
                            size="small"
                            sx={{
                              backgroundColor: stageColors[stage] || '#9e9e9e',
                              color: 'white',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={(client.resumeStatus || 'not_started').replace(/_/g, ' ')}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={(client.linkedinStatus || 'not_started').replace(/_/g, ' ')}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 150 }}>
                          {progress === null ? (
                            <Typography variant="caption" color="text.secondary">
                              Not on the pipeline
                            </Typography>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{
                                  flexGrow: 1,
                                  height: 6,
                                  borderRadius: 3,
                                  backgroundColor: colors.gray200,
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: colors.primary,
                                  },
                                }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {progress}%
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>{client.assignedTo || '-'}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, client)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {isManager && (
          <MenuItem onClick={handleAssignTeamClick}>
            <GroupIcon sx={{ mr: 1 }} fontSize="small" />
            Assign Team
          </MenuItem>
        )}
        <MenuItem onClick={() => { handleMenuClose(); selectedClient && navigate(`/clients/${selectedClient.id}`) }}>
          <VisibilityIcon sx={{ mr: 1 }} fontSize="small" />
          View Details
        </MenuItem>
        {isJobSearchEligible(selectedClient) && (
          <MenuItem onClick={() => { handleMenuClose(); selectedClient && navigate(`/clients/${selectedClient.id}/opportunities`) }}>
            <BusinessCenterIcon sx={{ mr: 1 }} fontSize="small" />
            Front Door
          </MenuItem>
        )}
        {isManager && (
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1, fontSize: 18 }} />
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* Assign Team Dialog */}
      <AssignTeamDialog
        open={assignTeamDialogOpen}
        onClose={() => setAssignTeamDialogOpen(false)}
        client={selectedClient}
        onAssigned={handleTeamAssigned}
      />

      {/* Add Client Dialog */}
      <AddClientDialog
        open={addClientDialogOpen}
        onClose={() => setAddClientDialogOpen(false)}
        onAdd={handleAddClient}
      />

      {/* Delete Client Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Client</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently delete <strong>{clientToDelete?.name}</strong>. It cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
